import {
  Select,
  Spin,
  Space,
  Collapse,
  Button,
  Modal,
  Divider,
  Card,
  Typography,
  Grid,
  Tooltip,
  Input,
  Tag,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Param, Command } from '../../cliDataSchema';
import React, { useMemo, useState, useEffect } from 'react';
import { FormInstance } from 'antd';
import useGitlabOsepPipelineVariableSearchQuery from '../../hooks/query/useGitlabOsepPipelineVariableSearcheQuery';
import useSearchGitRemoteRefQuery from '../../hooks/query/useSearchGitRemoteRefQuery';
import ParamFormItem from './ParamFormItem';
import BranchTagSelect from '../inputs/BranchTagSelect';
import InlineMarkdown from '../InlineMarkdown';
import config from '../../config';

type Props = {
  param: Param;
  visibleParams: Param[];
  form: FormInstance;
  command?: Command;
};

const OSEPPipelines = ({ param, visibleParams, form, command }: Props) => {
  const [disabled, setDisabled] = useState(false);
  const [localRefValue, setLocalRefValue] = useState<string>('');
  const [pendingRefValue, setPendingRefValue] = useState<string>('');
  const [localSelectedVariables, setLocalSelectedVariables] = useState<
    string[]
  >([]);
  const [variableSearchText, setVariableSearchText] = useState<string>('');
  const [showInputWarning, setShowInputWarning] = useState(false);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const gitRefParam = visibleParams.find(
    (p) => p.name === 'osep_pipeline_git_reference',
  );
  const cliDefaultRef = (gitRefParam?.default?.value as string) || '';

  const parseDefaultValue = (rawValue: unknown): string => {
    if (rawValue === null || rawValue === undefined || rawValue === 'null') {
      return '';
    }
    return String(rawValue);
  };

  useEffect(() => {
    if (cliDefaultRef && !localRefValue) {
      setLocalRefValue(cliDefaultRef);
      setPendingRefValue(cliDefaultRef);
      form.setFieldValue('osep_pipeline_git_reference', cliDefaultRef);
    }
  }, [cliDefaultRef, form, localRefValue]);

  const {
    data: gitRemoteRef,
    isLoading: isLoadingRefs,
    error: refError,
  } = useSearchGitRemoteRefQuery({
    repoUrl: config.gitlabPipelinePath,
    internalRepo: true,
  });

  const {
    data: pipelineVariables,
    isLoading,
    error,
  } = useGitlabOsepPipelineVariableSearchQuery({
    ref: localRefValue || '',
    gitlabCiFile: command?.osep_generator_ui_gitlab_ci_file || '',
  });

  const variableOptions = useMemo(() => {
    const options: Array<{
      label: React.ReactNode;
      value: string;
      isNew?: boolean;
      disabled?: boolean;
    }> = [];

    if (Array.isArray(pipelineVariables)) {
      pipelineVariables.forEach((item) => {
        if (typeof item === 'object' && item !== null) {
          Object.keys(item).forEach((variableName) => {
            const variableInfo = (item as Record<string, any>)[variableName];
            options.push({
              label: (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    width: '100%',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: isMobile ? '90vw' : '100%',
                  }}
                >
                  <Typography.Text
                    ellipsis={!isMobile}
                    style={{ width: '100%' }}
                  >
                    {variableName}
                  </Typography.Text>
                  {variableInfo?.description && (
                    <Typography.Text
                      type="secondary"
                      style={{
                        fontSize: isMobile ? '13px' : '12px',
                        fontWeight: 'normal',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        maxWidth: isMobile ? '90vw' : '100%',
                      }}
                    >
                      <InlineMarkdown>
                        {variableInfo.description}
                      </InlineMarkdown>
                    </Typography.Text>
                  )}
                </div>
              ),
              value: variableName,
            });
          });
        }
      });
    }

    if (
      variableSearchText &&
      !options.some(
        (opt) => opt.value.toLowerCase() === variableSearchText.toLowerCase(),
      ) &&
      !localSelectedVariables.includes(variableSearchText)
    ) {
      options.unshift({
        label: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography.Text>{variableSearchText}</Typography.Text>
            <Tag color="green" style={{ marginLeft: 8 }}>
              custom
            </Tag>
          </div>
        ),
        value: variableSearchText,
        isNew: true,
      });
    }

    return options;
  }, [pipelineVariables, variableSearchText, localSelectedVariables, isMobile]);

  const getVariableInfo = (variableName: string) => {
    if (!Array.isArray(pipelineVariables)) return null;

    for (const item of pipelineVariables) {
      if (typeof item === 'object' && variableName in item) {
        return (item as Record<string, any>)[variableName];
      }
    }
    return null;
  };

  const updateFormattedVariables = (variables: string[]) => {
    const formatted = variables.map((varName) => {
      const value =
        form.getFieldValue(`osep_pipeline_variables_${varName}`) ?? '';
      return `${varName}:${value}`;
    });
    form.setFieldValue('osep_pipeline_variables', formatted);
  };

  if (param.name === 'osep_pipeline_git_reference') {
    return null;
  }

  if (param.name === 'osep_pipeline_variables') {
    return (
      <>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {gitRefParam && (
            <ParamFormItem
              param={gitRefParam}
              disabled={disabled}
              setDisabled={setDisabled}
              visibleParams={visibleParams}
            >
              <BranchTagSelect
                value={pendingRefValue}
                disabled={disabled}
                data={gitRemoteRef}
                isLoading={isLoadingRefs}
                error={refError}
                placeholder="Select a git reference"
                defaultRef={cliDefaultRef}
                onChange={(selectedRef) => {
                  if (
                    selectedRef !== localRefValue &&
                    localSelectedVariables.length > 0
                  ) {
                    Modal.confirm({
                      title: 'Reset Variables?',
                      content:
                        'Changing the Git Reference will reset all selected variables. Do you want to continue?',
                      okText: 'Yes, reset',
                      cancelText: 'Cancel',
                      onOk: () => {
                        setPendingRefValue(selectedRef);
                        setLocalRefValue(selectedRef);
                        form.setFieldValue(
                          'osep_pipeline_git_reference',
                          selectedRef,
                        );
                        setLocalSelectedVariables([]);
                        form.setFieldValue('osep_pipeline_variables', []);
                      },
                      onCancel: () => {
                        setPendingRefValue(localRefValue);
                        form.setFieldValue(
                          'osep_pipeline_git_reference',
                          localRefValue,
                        );
                      },
                    });
                  } else {
                    setPendingRefValue(selectedRef);
                    setLocalRefValue(selectedRef);
                    form.setFieldValue(
                      'osep_pipeline_git_reference',
                      selectedRef,
                    );
                  }
                }}
              />
            </ParamFormItem>
          )}

          <ParamFormItem
            param={param}
            disabled={disabled}
            setDisabled={setDisabled}
            visibleParams={visibleParams}
          >
            <Spin spinning={isLoading}>
              <Tooltip
                title="Only letters (A-Z), numbers (0-9), underscores (_), and spaces are allowed"
                open={showInputWarning}
                placement="top"
                color="#FFDD6C"
              >
                <Select
                  placeholder="Select variables"
                  disabled={disabled || !localRefValue}
                  options={variableOptions.map((opt) => ({
                    ...opt,
                    className: localSelectedVariables.includes(opt.value)
                      ? 'osep-pipeline-variable-selected'
                      : undefined,
                  }))}
                  status={error ? 'error' : undefined}
                  value={null}
                  onChange={() => {}}
                  classNames={{
                    popup: { root: 'osep-pipeline-variables-dropdown' },
                  }}
                  onSearch={(value) => {
                    const hasInvalidChars = /[^a-zA-Z0-9_\s]/.test(value);
                    if (hasInvalidChars) {
                      setShowInputWarning(true);
                      setTimeout(() => setShowInputWarning(false), 10000);
                    }

                    const sanitized = value
                      .replace(/\s+/g, '_')
                      .replace(/[^a-zA-Z0-9_]/g, '')
                      .toUpperCase();
                    setVariableSearchText(sanitized);
                  }}
                  searchValue={variableSearchText}
                  onSelect={(value) => {
                    setVariableSearchText('');
                    setShowInputWarning(false);
                    if (value) {
                      if (localSelectedVariables.includes(value)) {
                        const newValues = localSelectedVariables.filter(
                          (v) => v !== value,
                        );
                        setLocalSelectedVariables(newValues);
                        form.setFieldValue(
                          `osep_pipeline_variables_${value}`,
                          undefined,
                        );
                        updateFormattedVariables(newValues);
                      } else {
                        const newValues = [...localSelectedVariables, value];
                        setLocalSelectedVariables(newValues);
                        const variableInfo = getVariableInfo(value);
                        const defaultValue = parseDefaultValue(
                          variableInfo?.default_value,
                        );
                        form.setFieldValue(
                          `osep_pipeline_variables_${value}`,
                          defaultValue,
                        );
                        updateFormattedVariables(newValues);
                      }
                    }
                  }}
                  onDropdownVisibleChange={(open) => {
                    if (!open) {
                      setVariableSearchText('');
                      setShowInputWarning(false);
                    }
                  }}
                  style={{ width: '100%' }}
                  showSearch
                  filterOption={(input, option) => {
                    if ((option as any)?.isNew) return true;
                    return (option?.value as string)
                      ?.toLowerCase()
                      .includes(input.toLowerCase());
                  }}
                />
              </Tooltip>
            </Spin>
          </ParamFormItem>
        </Space>

        <Divider />

        <Collapse
          defaultActiveKey={['1']}
          items={[
            {
              key: '1',
              label: `Selected Variables (${localSelectedVariables.length})`,
              children:
                localSelectedVariables.length > 0 ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {localSelectedVariables.map((variableName: string) => {
                      const variableInfo = getVariableInfo(variableName);
                      const defaultValue = parseDefaultValue(
                        variableInfo?.default_value,
                      );

                      return (
                        <Card
                          key={variableName}
                          styles={{
                            header: {
                              padding: isMobile ? '12px 16px' : '16px 24px',
                            },
                            body: {
                              padding: isMobile ? '12px 16px' : '16px 24px',
                            },
                          }}
                          title={
                            <Space direction="vertical" size={4}>
                              <Space>
                                <Typography.Text strong>
                                  {variableName}
                                </Typography.Text>
                                {!variableInfo && (
                                  <Tag color="green">custom</Tag>
                                )}
                              </Space>
                              {variableInfo?.description && (
                                <Typography.Text
                                  type="secondary"
                                  style={{
                                    fontSize: '12px',
                                    fontWeight: 'normal',
                                    wordBreak: 'break-word',
                                    whiteSpace: 'normal',
                                  }}
                                >
                                  <InlineMarkdown>
                                    {variableInfo.description}
                                  </InlineMarkdown>
                                </Typography.Text>
                              )}
                            </Space>
                          }
                          extra={
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => {
                                const newValues = localSelectedVariables.filter(
                                  (v) => v !== variableName,
                                );
                                setLocalSelectedVariables(newValues);
                                form.setFieldValue(
                                  `osep_pipeline_variables_${variableName}`,
                                  undefined,
                                );
                                updateFormattedVariables(newValues);
                              }}
                            />
                          }
                        >
                          {variableInfo?.type === 'bool' ||
                          variableInfo?.type === 'boolean' ? (
                            <Select
                              defaultValue={
                                defaultValue === 'true'
                                  ? 'true'
                                  : defaultValue === 'false'
                                    ? 'false'
                                    : undefined
                              }
                              disabled={disabled}
                              style={{ width: '100%' }}
                              options={[
                                { label: 'true', value: 'true' },
                                { label: 'false', value: 'false' },
                              ]}
                              onChange={(value) => {
                                const fieldName = `osep_pipeline_variables_${variableName}`;
                                form.setFieldValue(fieldName, value);
                                updateFormattedVariables(
                                  localSelectedVariables,
                                );
                              }}
                            />
                          ) : (
                            <Input
                              type="text"
                              defaultValue={defaultValue}
                              disabled={disabled}
                              onChange={(e) => {
                                const fieldName = `osep_pipeline_variables_${variableName}`;
                                form.setFieldValue(fieldName, e.target.value);
                                updateFormattedVariables(
                                  localSelectedVariables,
                                );
                              }}
                            />
                          )}
                        </Card>
                      );
                    })}
                  </Space>
                ) : (
                  <Typography.Text type="secondary" italic>
                    No variables selected. Select variables above to configure
                    their default values.
                  </Typography.Text>
                ),
            },
          ]}
        />
      </>
    );
  }

  return null;
};

export default OSEPPipelines;
